import MaturityBadge from './MaturityBadge'

interface SyncData {
  lender_name: string
  current_balance: number
  payments_count: number
  last_payment_date: string | null
}

interface Props {
  id: number
  lenderName: string
  loanAmount: number
  interestRate: number
  monthlyPayment: number
  originationPoints: number
  daysToMaturity: number
  maturityUrgency: 'red' | 'yellow' | 'green'
  maturityDate: string
  loanType: string
  autoSynced: boolean
  syncData?: SyncData
  onEdit: () => void
  onDelete: () => void
  onExitStrategy?: () => void
  exitStrategyLoading?: boolean
  isPro: boolean
}

const BORDER_COLORS = {
  red: 'border-l-red-500',
  yellow: 'border-l-amber-400',
  green: 'border-l-emerald-500',
} as const

const LOAN_TYPE_LABELS: Record<string, string> = {
  bridge: 'Bridge',
  dscr: 'DSCR',
  interest_only: 'Interest Only',
  amortizing: 'Amortizing',
  other: 'Other',
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function LoanCard({
  lenderName,
  loanAmount,
  interestRate,
  monthlyPayment,
  originationPoints,
  daysToMaturity,
  maturityUrgency,
  maturityDate,
  loanType,
  autoSynced,
  syncData,
  onEdit,
  onDelete,
  onExitStrategy,
  exitStrategyLoading,
  isPro,
}: Props) {
  return (
    <div className={`bg-white rounded-xl border border-dl-border border-l-4 ${BORDER_COLORS[maturityUrgency]} p-4`}>
      {/* Header row */}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 truncate">{lenderName}</p>
            <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-1.5 py-0.5 rounded">
              {LOAN_TYPE_LABELS[loanType] || loanType}
            </span>
            {autoSynced && syncData && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-dl-sync-bg text-dl-sync-text border border-dl-sync-border">
                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                </svg>
                Managed by {syncData.lender_name}
              </span>
            )}
          </div>
        </div>
        <MaturityBadge daysToMaturity={daysToMaturity} urgency={maturityUrgency} size="md" />
      </div>

      {/* 4-column data grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <div>
          <p className="text-[10px] text-slate-400 font-medium uppercase">Balance</p>
          <p className="text-sm font-semibold text-gray-900">
            {autoSynced && syncData ? formatCurrency(syncData.current_balance) : formatCurrency(loanAmount)}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-slate-400 font-medium uppercase">Rate</p>
          <p className="text-sm font-semibold text-gray-900">{interestRate}%</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-400 font-medium uppercase">Monthly</p>
          <p className="text-sm font-semibold text-gray-900">{formatCurrency(monthlyPayment)}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-400 font-medium uppercase">Points</p>
          <p className="text-sm font-semibold text-gray-900">{originationPoints || 0}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
        <p className="text-xs text-slate-400">
          Matures {formatDate(maturityDate)}
        </p>
        <div className="flex gap-1">
          {daysToMaturity <= 90 && onExitStrategy && isPro && (
            <button
              onClick={onExitStrategy}
              disabled={exitStrategyLoading}
              className="text-xs text-indigo-500 hover:text-indigo-700 font-medium px-1.5 py-0.5 disabled:opacity-50"
            >
              {exitStrategyLoading ? 'Analyzing...' : 'Exit strategy'}
            </button>
          )}
          <button onClick={onEdit} className="text-xs text-slate-400 hover:text-gray-600 px-1.5 py-0.5">
            Edit
          </button>
          <button onClick={onDelete} className="text-xs text-slate-400 hover:text-red-500 px-1.5 py-0.5">
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}
